import type { Client, Scope } from "@sentry/types";

export interface SurfaceSentry {
  getClient(): Client | undefined;
  // Add any non-Client type methods here that you want to proxy
  withScope(callback: (scope: Scope) => void): void;
  setTag(key: string, value: string): void;
  setContext(key: string, value: object): void;
}
/**
 * Whether Sentry is disabled.
 * This is used to prevent errors from being thrown when Sentry is disabled
 */
let isDisabled = false;

/**
 * This is a list of methods that we want to proxy from the surface Sentry instance,
 * in addition to the standard Client methods.
 */
type AdditionalSentryMethods = Omit<SurfaceSentry, "getClient">;

let surfaceSentry: null | SurfaceSentry = null;
export function exposeSurfaceSentryInstance<T extends SurfaceSentry>(
  surface: T,
) {
  surfaceSentry = surface;
}

export function setSentryDisabled(shouldBeDisabled: boolean) {
  isDisabled = shouldBeDisabled;
}

const additionalMethods: (keyof AdditionalSentryMethods)[] = [
  "withScope",
  "setTag",
  "setContext",
];

/**
 * Proxy that automatically routes Client methods to the surface Sentry instance,
 * without having to call `getClient()` first. Also exposes additional methods
 * that are not part of the Client interface but are useful for interacting with
 * Sentry.
 */
const clientProxy = new Proxy(
  {},
  {
    get(_, property, receiver) {
      if (isDisabled) {
        return () => {};
      }
      if (!surfaceSentry) {
        throw new Error(
          `You must provide the surface's Sentry client before interacting with the wrapper.`,
        );
      }
      if (
        additionalMethods.includes(property as keyof AdditionalSentryMethods)
      ) {
        return surfaceSentry[property as keyof AdditionalSentryMethods].bind(
          surfaceSentry,
        );
      }
      const target = surfaceSentry.getClient();
      if (!target) {
        throw new Error("Surface did not return a valid Sentry client!");
      }
      return Reflect.get(target, property, receiver);
    },
  },
) as Readonly<Client> & AdditionalSentryMethods;

export default clientProxy;

export const SENTRY_IGNORE_ERRORS = {
  NETWORK: [
    /TypeError: (Failed to fetch|fetch failed|terminated|NetworkError|Load failed)/,
    /Response failed with a \d+ code/,
    /(ERR_NETWORK_CHANGED|ERR_CONNECTION_RESET)/,
    "DropboxResponseError",
    "DOMException: The operation was aborted due to timeout",
  ],
  MIRAGE: [
    "Missing access token",
    "Did not receive a connection response",
    "Failed to find dependency",
  ],
  HORNET_MAIN: [
    /Attempting to get window state for (hornet-persistent|hornet-launcher) but it has been destroyed/,
  ],
  RESIZE_OBSERVER: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
  ],
  DESKTOP_AUTO_UPDATE: [
    "Could not connect to the server",
    "OSStatus error -60005", // User canceled the update
    "OSStatus error -60006", // User does not have admin privileges
  ],
  EXTENSION_CONTENT_SCRIPT: [
    "The message port closed before a response was received", // Happens in runtime messaging
    "Could not establish connection. Receiving end does not exist.", // Happens when sending messages to a closed tab
    "Load failed", // Content scripts may be blocked on some sites
    "Extension context invalidated", // Happens when the extension is updated/reloaded
  ],
  EXTENSION_POPUP: [
    "Script error.", // Often happens when third-party scripts block logs
    "Blocked a frame with origin", // Happens when embedding cross-origin iframes
    "SecurityError: The operation is insecure.", // Happens with `localStorage` in some contexts
  ],
};
